import { Button, Input } from '@breetometer/components';
import { DialogAccessCredentials, ModalParts } from 'BreetComponents';
import {
	DEFAULT_INPUT_MAX_LENGTH,
	type IUseAccessCredentialsRequest,
	type NetworkType,
	OTPLENGTH,
	showToast,
	type TokenType,
} from 'BreetHelpers';
import { useAccessCredentials, useFormData, useFormStage } from 'BreetHooks';
import { closeModal, selectModals, useAppDispatch, useAppSelector, useSaveWalletAddressMutation } from 'BreetRedux';
import { useCallback } from 'react';

import { WalletAddressNameSchema, type WalletAddressNameSTAGE } from './DialogEditWalletAddressName.helpers';

const DialogEditWalletAddressName = () => {
	const { navigator, isStageActive } = useFormStage<WalletAddressNameSTAGE>('wallet-address-name', [
		'wallet-address-name',
		'otp-verification',
	]);

	const dispatch = useAppDispatch();

	const [saveWalletAddress, { isLoading }] = useSaveWalletAddressMutation();

	const modals = useAppSelector(selectModals);

	const selectedModal = modals.find((modal) => modal.id === 'editWalletAddressName');

	const walletAddressMeta = selectedModal?.metadata as
		| { label: string; address: string; token: TokenType; network: NetworkType }
		| undefined;

	const walletAddressNameFormDataContextProps = useFormData(
		WalletAddressNameSchema,
		{ walletAddressName: walletAddressMeta?.label },
		{ mode: 'onChange' }
	);

	const submitWalletAddressName = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!walletAddressMeta) {
				showToast('error', 'Wallet address meta not found');
				dispatch(closeModal('editWalletAddressName'));
				return;
			}

			saveWalletAddress({
				address: walletAddressMeta.address,
				token: walletAddressMeta.token,
				network: walletAddressMeta.network,
				label: walletAddressNameFormDataContextProps.hookFormProps.watch('walletAddressName'),
			})
				.unwrap()
				.then(() => {
					showToast('success', 'Wallet address name updated successfully');
					data?.onSuccess?.();
					dispatch(closeModal('editWalletAddressName'));
				})
				.catch((e: unknown) => data?.onError(e));
		},
		[dispatch, saveWalletAddress, walletAddressMeta, walletAddressNameFormDataContextProps.hookFormProps]
	);

	const { component: OTPComponent } = useAccessCredentials({
		id: 'edit-wallet-address-name',
		mode: 'otp',
		submitRequest: submitWalletAddressName,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify OTP',
		description: `Enter the ${OTPLENGTH}-digit OTP sent to your email to edit the wallet address name.`,
		type: 'verify',
		render: (props) => <DialogAccessCredentials {...props} />,
	});

	return (
		<ModalParts.Wrapper>
			<ModalParts.Stage hide={!isStageActive('wallet-address-name')}>
				<div className='appdeveloper_card edit-wallet-address-name column bg-primary-500 border-dark-navy'>
					<form
						className='appdeveloper_form dialog-form'
						onSubmit={walletAddressNameFormDataContextProps.hookFormProps.handleSubmit(navigator.goTo('otp-verification'))}
					>
						<Input
							label='Edit Wallet Address Name'
							id='walletAddressName'
							required
							widthType='full'
							autoComplete='off'
							placeholder=' '
							maxLength={DEFAULT_INPUT_MAX_LENGTH}
							{...walletAddressNameFormDataContextProps.hookFormProps.register('walletAddressName')}
							errorMessage={walletAddressNameFormDataContextProps.getFieldError('walletAddressName')?.message}
						/>

						<Button
							label='Save'
							className='primary-hover'
							type='submit'
							disabled={
								!walletAddressNameFormDataContextProps.isFormCompleted ||
								!walletAddressNameFormDataContextProps.hookFormProps.formState.isDirty ||
								isLoading
							}
							isLoading={isLoading}
						/>
					</form>
				</div>
			</ModalParts.Stage>

			<ModalParts.Stage hide={!isStageActive('otp-verification')}>{OTPComponent}</ModalParts.Stage>
		</ModalParts.Wrapper>
	);
};

export default DialogEditWalletAddressName;
